import React, { useMemo } from "react";

import {
	ActionItemsRecordingContext,
	type ActionItemsRecordingContextType,
	ActionItemsState,
} from "~/actionItems/newActionItems/ActionItemsContext";
import ErrorBoundary from "~/common/ErrorBoundary";
import { RateLimitError } from "~/types/errors";

import SyntheticActionItemGroupsList from "../actionItems/actionItemGroup/SyntheticActionItemGroupsList";

function ActionItemsListErrorFallback({ error }: { error: Error }) {
	// When a 429 happens we throw an error with the message "Rate limit exceeded" in httpFetch in models/index.ts
	const isRateLimit = error instanceof RateLimitError;
	return (
		<p style={{ font: "var(--paragraph-4)" }}>
			{isRateLimit
				? "You reached the api rate limit of your workspace. Failed to load action items."
				: "Something went wrong while loading action items."}
		</p>
	);
}

export default function AIActionItemsList({
	noteId,
	recordingId,
	videoDuration,
	aiDetectedActionItemsListRefetchKey,
	onTimestampClick,
}: {
	noteId: string;
	recordingId: string;
	videoDuration: number;
	aiDetectedActionItemsListRefetchKey: number;
	onTimestampClick: (timestamp: number) => void;
}) {
	const specificContextValues = useMemo<ActionItemsRecordingContextType>(() => {
		return {
			noteId,
			recordingId,
			videoDuration,
			onTimestampClick,
		};
	}, [noteId, recordingId, videoDuration, onTimestampClick]);

	return (
		<ActionItemsRecordingContext.Provider value={specificContextValues}>
			<ActionItemsState
				namespace="ai_highlights"
				filters={{
					showCompleted: true,
					showWontDo: true,
					showRecentOnly: false,
					showAIDetected: true,
				}}
			>
				{/* Wrapping in ErrorBoundary to handle rate limit errors and avoid crash the whole page */}
				<ErrorBoundary fallbackComponent={ActionItemsListErrorFallback}>
					<SyntheticActionItemGroupsList
						fetchKey={`actionitems-${recordingId}-${aiDetectedActionItemsListRefetchKey}`}
					/>
				</ErrorBoundary>
			</ActionItemsState>
		</ActionItemsRecordingContext.Provider>
	);
}
